/**
 * URL-first Active Projects navigation helpers.
 * Walks vendorDetails[].vendorList[] (merged assessment rows) for eligibility and tab alignment.
 */

import { getActiveAssessment } from "@/utils/productInfoDetailsSelectors";
import { getStatusConfigEntry } from "@/utils/helpers/vendorRedirection";
import { isReadyForOrders } from "@/utils/activeProjectStatusUtils";
import {
  resolveProductLevelStatusForPackCopyTab,
  vendorMeetsArtworkFileTabVisibilityCriteria,
  vendorMeetsPackCopyTabVisibilityCriteria,
} from "@/components/ActiveProjects/PackCopy/logic/packCopyVendorStep";

export type VendorAssessmentRow = {
  groupIndex: number;
  versionIndex: number;
  vendorId: string;
  assessmentId: string;
  vendorAssessmentStatus: string;
};

function asRecord(p: unknown): Record<string, unknown> | null {
  if (p != null && typeof p === "object") return p as Record<string, unknown>;
  return null;
}

/** Flatten vendor group × version rows with stable ordering (group order, then vendorList index). */
export function enumerateVendorAssessmentRows(product: unknown): VendorAssessmentRow[] {
  const rows: VendorAssessmentRow[] = [];
  const rec = asRecord(product);
  const vds = rec?.vendorDetails;
  if (!Array.isArray(vds)) return rows;

  for (let g = 0; g < vds.length; g += 1) {
    const group = vds[g];
    if (group == null || typeof group !== "object") continue;
    const go = group as Record<string, unknown>;
    const vendorId = String(go.vendorId ?? go.id ?? "").trim();
    const list = Array.isArray(go.vendorList) ? go.vendorList : [];
    if (list.length === 0 || !vendorId) continue;

    for (let v = 0; v < list.length; v += 1) {
      const merged = getActiveAssessment(rec, g, v) as Record<string, unknown> | null;
      if (!merged) continue;
      const st = String(merged.vendorAssessmentStatus ?? "").trim();
      const listRow = list[v] as Record<string, unknown>;
      const aidRaw = merged.assessmentId ?? merged.vendorAssessmentId ?? listRow.assessmentId ?? listRow.vendorAssessmentId;
      const aid = aidRaw != null && String(aidRaw).trim() !== "" ? String(aidRaw).trim() : "";
      if (!aid) continue;
      rows.push({
        groupIndex: g,
        versionIndex: v,
        vendorId,
        assessmentId: aid,
        vendorAssessmentStatus: st,
      });
    }
  }
  return rows;
}

/** `{ groupIndex, versionIndex }` for Active Projects detail when landing with URL query params. */
export type ActiveProjectsUrlVendorVersionResolution = {
  groupIndex: number;
  versionIndex: number;
};

/**
 * Scan `vendorDetails` / `vendorList` (same shape as list + product-info) for URL `vendorId` and/or
 * `vendorAssessmentId` and return vendor strip + version indices.
 *
 * - **TJ + both ids:** row must match **both** `vendorId` and `vendorAssessmentId`.
 * - **TJ + assessment only:** first row whose assessment id matches.
 * - **Vendor user + assessment:** row must match assessment and **logged-in** `vendorId` scope.
 * - **`vendorId` only:** first enumerated row for that vendor account.
 */
export function findVendorVersionIndicesFromActiveProjectsUrl(
  product: unknown,
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId: string | null | undefined,
  opts?: { isVendorUser?: boolean; loggedInVendorId?: string | null }
): ActiveProjectsUrlVendorVersionResolution | null {
  const rows = enumerateVendorAssessmentRows(product);
  const vid = (urlVendorId ?? "").trim();
  const aid = (urlVendorAssessmentId ?? "").trim();
  const isVu = Boolean(opts?.isVendorUser);
  const scopeVid = (opts?.loggedInVendorId ?? "").trim();

  if (rows.length === 0) return null;

  if (aid) {
    for (const row of rows) {
      if (String(row.assessmentId) !== aid) continue;
      if (isVu) {
        if (scopeVid && String(row.vendorId) !== scopeVid) continue;
      } else if (vid && String(row.vendorId) !== vid) {
        continue;
      }
      return { groupIndex: row.groupIndex, versionIndex: row.versionIndex };
    }
    return null;
  }

  if (vid) {
    const found = rows.find((r) => String(r.vendorId) === vid);
    if (found) return { groupIndex: found.groupIndex, versionIndex: found.versionIndex };
  }

  return null;
}

/**
 * True when this assessment should be browsed on /active-projects/* (not RFO, shopping list, VA, etc.).
 * Unknown statuses (no status-config entry) are ineligible for AP detail navigation.
 */
export function isVendorAssessmentEligibleForActiveProjectsRoute(
  status: string | null | undefined
): boolean {
  if (!status || String(status).trim() === "") return false;
  if (isReadyForOrders(status)) return false;
  const entry = getStatusConfigEntry(status);
  if (!entry) return false;
  const url = String(entry.redirect_url || "").trim();
  return url === "/active-projects" || url.startsWith("/active-projects/");
}

/**
 * True when the merged assessment row is valid for the given main tab on Active Projects.
 * PACK_COPY / ARTWORK_FILE reuse the same criteria as staticTabs visibility (per-vendor). Pack Copy is gated on
 * `vendorAssessmentStatus` step ≥ 10 only (no workflow shortcut — avoids group-inherited workflow on merged rows).
 */
export function isAssessmentRowAlignedWithTabKey(
  product: Record<string, unknown> | null | undefined,
  merged: Record<string, unknown>,
  tabKey: string
): boolean {
  const status = String(merged.vendorAssessmentStatus ?? "");
  if (!isVendorAssessmentEligibleForActiveProjectsRoute(status)) return false;

  const projectStatus = resolveProductLevelStatusForPackCopyTab(product ?? undefined);

  if (tabKey === "PACK_COPY") {
    return vendorMeetsPackCopyTabVisibilityCriteria(merged, projectStatus);
  }
  if (tabKey === "ARTWORK_FILE") {
    return vendorMeetsArtworkFileTabVisibilityCriteria(merged, projectStatus);
  }
  return true;
}

/** First vendorList row (scan order) whose status belongs on Active Projects detail. */
export function findFirstApplicableVendorAssessmentForActiveProjects(
  product: unknown
): VendorAssessmentRow | null {
  for (const row of enumerateVendorAssessmentRows(product)) {
    if (isVendorAssessmentEligibleForActiveProjectsRoute(row.vendorAssessmentStatus)) {
      return row;
    }
  }
  return null;
}

export function buildActiveProjectsDetailHref(
  productId: string | number,
  vendorId: string,
  assessmentId: string,
  tab?: string | null
): string {
  const base = `/active-projects/${encodeURIComponent(String(productId))}`;
  const q = new URLSearchParams();
  q.set("vendorAssessmentId", assessmentId);
  q.set("vendorId", vendorId);
  if (tab != null && String(tab).trim() !== "") {
    q.set("tab", String(tab).trim());
  }
  return `${base}?${q.toString()}`;
}

/**
 * When status-config targets Active Projects and defines a string `tab` query slug
 * (e.g. `artworkFile` for Artwork in Design), return it for detail URLs. Numeric `tab` values are
 * omitted — they are not mapped by the main-tab URL effect in staticTabs.
 */
export function getStatusConfigTabSlugForActiveProjectsDetailUrl(
  vendorAssessmentStatus: string | null | undefined
): string | null {
  if (!vendorAssessmentStatus || String(vendorAssessmentStatus).trim() === "") return null;
  if (!isVendorAssessmentEligibleForActiveProjectsRoute(vendorAssessmentStatus)) return null;
  const entry = getStatusConfigEntry(String(vendorAssessmentStatus)) as {
    tab?: string | number;
  } | undefined;
  if (!entry || entry.tab == null) return null;
  if (typeof entry.tab !== "string" || String(entry.tab).trim() === "") return null;
  return String(entry.tab).trim();
}

/** List / navigation: AP detail href + optional `tab` from status-config string slugs. */
export function buildActiveProjectsDetailHrefFromAssessmentPick(
  productId: string | number,
  pick: VendorAssessmentRow
): string {
  const tab = getStatusConfigTabSlugForActiveProjectsDetailUrl(pick.vendorAssessmentStatus);
  return buildActiveProjectsDetailHref(productId, pick.vendorId, pick.assessmentId, tab);
}

function rowIndexInFlat(
  flat: VendorAssessmentRow[],
  groupIndex: number,
  versionIndex: number
): number {
  return flat.findIndex((r) => r.groupIndex === groupIndex && r.versionIndex === versionIndex);
}

/** Next row after current that satisfies tab alignment; wraps to null if none. */
export function findNextVendorAssessmentAlignedWithTab(
  product: Record<string, unknown> | null | undefined,
  currentGroupIndex: number,
  currentVersionIndex: number,
  tabKey: string
): VendorAssessmentRow | null {
  if (!product) return null;
  const flat = enumerateVendorAssessmentRows(product);
  if (flat.length === 0) return null;

  const idx = rowIndexInFlat(flat, currentGroupIndex, currentVersionIndex);
  const start = idx >= 0 ? idx + 1 : 0;

  for (let i = start; i < flat.length; i += 1) {
    const r = flat[i];  
    if (!r) continue;
    const merged = getActiveAssessment(product, r.groupIndex, r.versionIndex) as Record<string, unknown> | null;
    if (merged && isAssessmentRowAlignedWithTabKey(product, merged, tabKey)) {
      return r;
    }
  }
  return null;
}

/** Previous row before current that satisfies tab alignment. */
export function findPreviousVendorAssessmentAlignedWithTab(
  product: Record<string, unknown> | null | undefined,
  currentGroupIndex: number,
  currentVersionIndex: number,
  tabKey: string
): VendorAssessmentRow | null {
  if (!product) return null;
  const flat = enumerateVendorAssessmentRows(product);
  if (flat.length === 0) return null;

  const idx = rowIndexInFlat(flat, currentGroupIndex, currentVersionIndex);
  const end = idx >= 0 ? idx - 1 : flat.length - 1;

  for (let i = end; i >= 0; i -= 1) {
    const r = flat[i];
    if (!r) continue;
    const merged = getActiveAssessment(product, r.groupIndex, r.versionIndex) as Record<string, unknown> | null;
    if (merged && isAssessmentRowAlignedWithTabKey(product, merged, tabKey)) {
      return r;
    }
  }
  return null;
}

/** First row in scan order aligned with tab (for landing after tab change). */
export function findFirstVendorAssessmentAlignedWithTab(
  product: Record<string, unknown> | null | undefined,
  tabKey: string
): VendorAssessmentRow | null {
  if (!product) return null;
  for (const r of enumerateVendorAssessmentRows(product)) {
    const merged = getActiveAssessment(product, r.groupIndex, r.versionIndex) as Record<string, unknown> | null;
    if (merged && isAssessmentRowAlignedWithTabKey(product, merged, tabKey)) {
      return r;
    }
  }
  return null;
}

/** Last row in scan order aligned with tab (e.g. Back into previous main tab). */
export function findLastVendorAssessmentAlignedWithTab(
  product: Record<string, unknown> | null | undefined,
  tabKey: string
): VendorAssessmentRow | null {
  if (!product) return null;
  const flat = enumerateVendorAssessmentRows(product);
  for (let i = flat.length - 1; i >= 0; i -= 1) {
    const r = flat[i];
    if (!r) continue;
    const merged = getActiveAssessment(product, r.groupIndex, r.versionIndex) as Record<string, unknown> | null;
    if (merged && isAssessmentRowAlignedWithTabKey(product, merged, tabKey)) {
      return r;
    }
  }
  return null;
}
